import Image from "next/image";
import { useEffect, useState } from "react";

import { useScrollDisabled } from "@/hooks/common/useScrollDisabled";
import closeIcon from "@/assets/icons/close-gray.svg";
import arrowIcon from "@/assets/icons/arrow_left.svg";
import clsx from "clsx";

type Props = {
  title: string;
  isDrawerOpen: boolean;
  setIsDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  children: React.ReactNode;
  leftHeaderBackButton?: {
    label?: string;
    clickHandler?: () => void;
  };
  rightHeaderButton?: {
    label: string;
    clickHandler: () => void;
  };
  rightHeaderLabel?: JSX.Element | string;
  height?: string;
};

/* ドロワー */
//MEMO: ひとまず下から出てくるタイプのみ
const Drawer: React.FC<Props> = ({
  title,
  isDrawerOpen,
  setIsDrawerOpen,
  children,
  leftHeaderBackButton,
  rightHeaderButton,
  rightHeaderLabel,
  height = "h-[90svh]"
}) => {
  const { setIsLocked } = useScrollDisabled();
  const [isFirstRender, setIsFirstRender] = useState(true);

  // ドロワー以外の領域タップで閉じるハンドラー
  const handleBackdropClick = () => {
    setIsDrawerOpen(false);
  };

  // ドロワーが開いたら最上位のスクロールを無効にする
  useEffect(() => {
    setIsLocked(isDrawerOpen);
    if (isDrawerOpen) setIsFirstRender(false);
  }, [isDrawerOpen]);

  return (
    <div className="relative">
      {/* 背景 */}
      <div
        className={`${
          isDrawerOpen && !isFirstRender
            ? "animate__animated animate__fadeIn animate__fastest"
            : "invisible"
        } fixed inset-0 z-20 bg-cLabelBlack/60`}
        onClick={handleBackdropClick}
      ></div>
      {/* ドロワー */}
      <div
        className={`${
          isDrawerOpen
            ? "animate__animated animate__slideInUp animate__fastest"
            : !isFirstRender
              ? "animate__animated animate__slideOutDown animate__fastest"
              : "invisible"
        } fixed inset-0 z-30 h-full w-full`}
        onClick={handleBackdropClick}
      >
        <div
          className={clsx(
            "absolute inset-x-0 bottom-0 w-full rounded-t-[1.2rem] bg-white pt-8",
            height
          )}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="relative border-b border-cBorderGray pb-16 pt-[0.7rem] text-center">
            {/* 閉じる/戻るボタン */}
            <div
              className={`absolute left-16 ${
                leftHeaderBackButton?.label || !leftHeaderBackButton
                  ? "top-[0.625rem]"
                  : "top-[0.5rem]"
              } cursor-pointer`}
              onClick={() => {
                if (leftHeaderBackButton?.clickHandler) {
                  leftHeaderBackButton?.clickHandler();
                } else {
                  setIsDrawerOpen(false);
                }
              }}
            >
              {leftHeaderBackButton?.label ? (
                <p className="text-md text-cErrorRed">
                  {leftHeaderBackButton.label}
                </p>
              ) : (
                <Image
                  src={leftHeaderBackButton ? arrowIcon : closeIcon}
                  alt={leftHeaderBackButton ? "戻る" : "閉じる"}
                  height={leftHeaderBackButton ? 28 : 24}
                  width={leftHeaderBackButton ? 28 : 24}
                />
              )}
            </div>
            {/* ヘッダタイトル */}
            <p className="mx-auto max-w-[27rem] truncate text-md text-cCaptionGray md:max-w-[90rem]">
              {title}
            </p>
            {/* ヘッダ右側に配置するボタン */}
            {rightHeaderButton ? (
              <button
                className="absolute right-16 top-8 text-md text-cErrorRed"
                onClick={rightHeaderButton.clickHandler}
              >
                {rightHeaderButton.label}
              </button>
            ) : rightHeaderLabel ? (
              <div className="absolute right-16 top-8 text-md">
                {rightHeaderLabel}
              </div>
            ) : null}
          </div>
          <div className="mx-auto max-h-full min-w-[30rem] max-w-[55rem] overflow-y-auto px-8">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Drawer;
